import isEmpty from "lodash/isEmpty";
import qs from "qs";

const _getPath = (path, params = {}) => {
  const { origin, pathname } = new URL(path);
  path = `${origin}/api/v2${pathname}`;

  if (!isEmpty(params))
    path = `${path}?${qs.stringify(params, { arrayFormat: "brackets" })}`;
  return path;
};

const _getHeaders = (token, headerOptions = {}) => {
  if (token) {
    return {
      "Content-Type": "application/json",
      "x-api-token": `Bearer ${token}`,
      ...headerOptions
    };
  }
  return {
    "Content-Type": "application/json",
    ...headerOptions
  };
};

export const get = async (token, queryPath, params, config) => {
  try {
    const res = await fetch(_getPath(queryPath, params), {
      headers: _getHeaders(token, config?.headers),
      credentials: "same-origin"
    });

    if (!res.ok) {
      throw `GET ${res.status} ${res.url}`;
    }

    return res.json();
  } catch (err) {
    throw new Error(err);
  }
};

export const post = async (token, queryPath, body) => {
  try {
    const res = await fetch(_getPath(queryPath), {
      method: "post",
      headers: _getHeaders(token),
      body: JSON.stringify(body),
      credentials: "same-origin"
    });
    if (res.status === 204 && res.ok) {
      // handling 204 status empty response
      return { ok: res.ok };
    }

    if (!res.ok) {
      throw `POST ${res.status} ${res.url}`;
    }

    return res.json();
  } catch (err) {
    throw new Error(err);
  }
};

export const put = async (token, queryPath, body) => {
  try {
    const res = await fetch(_getPath(queryPath), {
      method: "put",
      headers: _getHeaders(token),
      body: JSON.stringify(body),
      credentials: "same-origin"
    });

    if (!res.ok) {
      throw `PUT ${res.status} ${res.url}`;
    }

    return res.text().length ? res.json() : {};
  } catch (err) {
    throw new Error(err);
  }
};

export const patch = async (token, queryPath, body) => {
  try {
    const res = await fetch(_getPath(queryPath), {
      method: "PATCH",
      headers: _getHeaders(token),
      body: JSON.stringify(body),
      credentials: "same-origin"
    });

    if (!res.ok) {
      throw `PATCH ${res.status} ${res.url}`;
    }

    return res.text().length ? res.json() : {};
  } catch (err) {
    throw new Error(err);
  }
};

export const extractResponseHeaders = (response) => {
  const object = {};
  response.headers.forEach((value, key) => {
    if (key === "set-cookie") return;
    object[key] = value;
  });

  const setCookies =
    typeof response.headers.getSetCookie === "function"
      ? response.headers.getSetCookie()
      : [];
  if (setCookies.length) {
    object["set-cookie"] = setCookies;
  } else {
    const fallback = response.headers.get("set-cookie");
    if (fallback) object["set-cookie"] = [fallback];
  }

  return object;
};

const _extractResponseHeaders = extractResponseHeaders;

export const _request = async (method, token, endpoint, data, config = {}) => {
  const options = {
    method,
    headers: _getHeaders(token, config?.headers),
    credentials: "same-origin",
    ...(config?.cache ? { cache: config.cache } : {})
  };

  if (config?.signal) {
    options.signal = config?.signal;
  }

  if (data) {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(endpoint, options);

  const payload = {
    status: response.status,
    statusText: response.statusText,
    headers: _extractResponseHeaders(response)
  };

  return response.text().then((text) => {
    try {
      payload.data = JSON.parse(text);
      // eslint-disable-next-line no-unused-vars
    } catch (err) {
      payload.data = undefined;
    } finally {
      if (response.ok) {
        // eslint-disable-next-line no-unsafe-finally
        return payload;
      }
      // eslint-disable-next-line no-unsafe-finally
      throw payload;
    }
  });
};

export const getData = (token, queryPath, params, config) => {
  return _request("GET", token, _getPath(queryPath, params), "", config);
};

export const postData = (token, queryPath, params, data, config) => {
  return _request("POST", token, _getPath(queryPath, params), data, config);
};

export const putData = (token, queryPath, params, data, config) => {
  return _request("PUT", token, _getPath(queryPath, params), data, config);
};

export const patchData = (token, queryPath, params, data, config) => {
  return _request("PATCH", token, _getPath(queryPath, params), data, config);
};

export const deleteData = (token, queryPath, params, config) => {
  return _request("DELETE", token, _getPath(queryPath, params), config);
};
